import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SessionService } from './session.service';
import { DocumentosService } from './documentos.service';
import { ProcessoService } from './processo.service';

/**
 * Aba "Análise Cearaprev" e aba "Peças" da tela de análise do processo
 * (Detalhamento de Requisitos - Militares / Consultar Benefício).
 *
 * Endpoints ainda não existem no backend — enquanto isso, USAR_MOCK responde
 * com dados locais. As chamadas reais já estão escritas; para integrar, basta
 * desligar a flag.
 */

export interface StatusBeneficioOpcao {
  value: string;
  label: string;
}

/**
 * Status do benefício — espelho do EnumStatusBeneficio do militar-back
 * (value = nome do enum aceito pela API), em ordem alfabética como a doc exige.
 */
export const STATUS_BENEFICIO: StatusBeneficioOpcao[] = [
  { value: 'ANALISE_PGE', label: 'Análise – PGE' },
  { value: 'ANALISE_SETORIAL', label: 'Análise Setorial' },
  { value: 'ANALISE_TCE', label: 'Análise – TCE' },
  { value: 'ASSINATURA_DO_ATO', label: 'Assinatura do Ato' },
  { value: 'ENCAMINHADO_TCE', label: 'Encaminhado TCE' },
  { value: 'IMPLANTACAO', label: 'Implantação' },
  { value: 'IMPLANTADO', label: 'Implantado' },
  { value: 'NOTIFICACAO', label: 'Notificação' },
  { value: 'NOTIFICADO', label: 'Notificado' },
  { value: 'PUBLICACAO', label: 'Publicação' },
  { value: 'PUBLICADO', label: 'Publicado' },
  { value: 'REGISTRADO', label: 'Registrado' },
].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

/** Tipos de documento anexáveis na aba Peças (doc, p. 6). */
export const TIPOS_DOCUMENTO_PECA: string[] = [
  'Ato publicado',
  'Ato tornando sem efeito',
  'Boletim de Inclusão',
  'Capa',
  'Certidão de Notificação',
  'Certidão de Tempo de Contribuição',
  'Comprovação Acréscimo Férias em Dobro',
  'Comprovação Acréscimo Licença Especial',
  'Comprovação Dedução',
  'Comprovante Residência',
  'Cota PGE',
  'CPF',
  'Declaração Processo Administrativo Disciplinar',
  'Despacho Cearaprev',
  'Despacho Setorial',
  'Diplomas, Certificados e Cursos',
  'Documento de Identificação',
  'DOE Concessão de Vantagem Pessoal',
  'DOE Gratificações/Vantagens',
  'Ficha funcional',
  'Informação – Reexame TCE',
  'Juntada de Documentos',
  'Laudo Pericial – Incapacidade/Invalidez',
  'Minuta de Ato',
  'Ofício TCE',
  'Parecer Jurídico',
  'Parecer PGE',
  'Quadro de Promoções',
  'Quadro Discriminativo de Tempo de Contribuição',
  'Relatório Voto TCE',
  'Requerimento de Reserva/Reforma',
  'Resolução TCE',
];

export interface DadosAnalise {
  status: string | null;
  /** Preenchida pela integração com a Casa Civil quando o ato é publicado. */
  dataUltimaPublicacao: string | null;
  /** Preenchida pela integração com o TCE. */
  dataRegistroTce: string | null;
}

export interface DatasAtoSemEfeito {
  dataAto: string;
  dataPublicacao: string;
}

@Injectable({ providedIn: 'root' })
export class AnaliseProcessoService {
  private urlServer = environment.URL_API;

  /** Enquanto os endpoints não existem, o serviço responde com dados de exemplo. */
  private readonly USAR_MOCK = false;

  constructor(
    private http: HttpClient,
    private session: SessionService,
    private documentosService: DocumentosService,
    private processoService: ProcessoService,
  ) {}

  private get headers(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${this.session.token}` });
  }

  /**
   * PATCH /processo/{idBeneficio}/analise — atualiza o status do benefício e
   * registra no histórico de status. Retorna o detalhe completo atualizado.
   */
  atualizarAnalise(
    idBeneficio: number | string,
    status: string,
    usuarioResponsavel?: string
  ): Observable<any> {
    return this.http.patch<any>(
      `${this.urlServer}/processo/${idBeneficio}/analise`,
      { status, usuarioResponsavel },
      { headers: this.headers }
    );
  }

  /** POST /beneficio/{id}/ato-sem-efeito/datas — modal "Adicionar Datas Ato Sem Efeito". */
  adicionarDatasAtoSemEfeito(
    idBeneficio: number | string,
    datas: DatasAtoSemEfeito
  ): Observable<{ ok: boolean }> {
    if (this.USAR_MOCK) {
      return of({ ok: true });
    }
    return this.http.post<{ ok: boolean }>(
      `${this.urlServer}/beneficio/${idBeneficio}/ato-sem-efeito/datas`,
      datas,
      { headers: this.headers }
    );
  }

  /** POST /beneficio/{id}/ato-sem-efeito — gera o ato tornando sem efeito. */
  gerarAtoSemEfeito(idBeneficio: number | string): Observable<{ ok: boolean }> {
    if (this.USAR_MOCK) {
      return of({ ok: true });
    }
    return this.http.post<{ ok: boolean }>(
      `${this.urlServer}/beneficio/${idBeneficio}/ato-sem-efeito`,
      {},
      { headers: this.headers }
    );
  }

  /** POST /beneficio/{id}/publicar-edweb — publica o ato no Edweb. */
  publicarAtoEdweb(idBeneficio: number | string): Observable<{ ok: boolean }> {
    if (this.USAR_MOCK) {
      return of({ ok: true });
    }
    return this.http.post<{ ok: boolean }>(
      `${this.urlServer}/beneficio/${idBeneficio}/publicar-edweb`,
      {},
      { headers: this.headers }
    );
  }

  /**
   * Insere uma peça (PDF) no processo, via POST /files/upload (endpoint real,
   * já usado pelas demais abas). O vínculo com o processo é feito pelo
   * idBeneficio.
   */
  inserirPeca(
    file: File,
    tipoDocumento: string,
    processo: any
  ): Observable<any> {
    const matricula = processo?.servidor?.matricula ?? '';
    const idOrgao = processo?.servidor?.idOrgaoSeplag ?? '';

    // O detalhe do processo lista documentos por isnOrigem = id do VÍNCULO
    // (DetalheProcessoServiceImpl.findByIsnOrigem). Sem o idOrigem correto,
    // a peça sobe mas nunca aparece na lista — por isso buscamos o vínculo antes.
    return this.processoService.getDadosAbertura(matricula, idOrgao).pipe(
      switchMap((dados) =>
        this.documentosService.uploadDocumento(file, {
          matricula,
          idOrgao,
          idOrigem: dados?.vinculo?.id,
          idBeneficio: processo?.idBeneficio ?? processo?.id,
          tipoDocumento,
        })
      )
    );
  }
}
